/**
 * Content identity, as the second axis of source independence.
 *
 * `url.ts` answers "are these two sources independent?" with DNS distinctness,
 * and says at length why every ambiguous case there resolves toward merging.
 * This file exists because DNS distinctness is a PROXY for editorial
 * independence, and syndication is precisely the condition under which the proxy
 * stops holding: one wire desk, one press officer or one publisher's five
 * versions of record put ONE artefact on many registrable domains, every copy
 * carrying the same quotable text. `docs/decisions.md` D23 walks that path end to
 * end and D28 records what this closes.
 *
 * So the gate needs a second question — is this the same TEXT? — and these are
 * the three answers it can give: identical after folding (`textHash`), near
 * identical (`simHash` plus `hamming`), or neither. Nothing here reads a URL, a
 * host or a model's opinion; it is arithmetic over the same folded text the quote
 * check already reads, which is what makes the collapse reproducible by hand from
 * the stored record.
 *
 * NOT cryptography, and the distinction is load-bearing. These are collapse keys
 * over one check's own source list, not authentication: a collision costs a
 * merge, and a merge costs a true refutation its loud channel — the legible
 * failure `url.ts` already chooses in every ambiguous case. FNV-1a is used
 * because it is short, exactly specified, dependency-free and identical in every
 * runtime this ships to.
 *
 * Imports nothing, like `models.ts`, and for a weaker version of the same reason:
 * `types.ts` calls into this from close to the boot cycle, and a registry or a
 * store reached from here would join it.
 */

/**
 * 64 bits, as two unsigned 32-bit halves, because JavaScript's numbers stop being
 * exact at 53 — and not as BigInt, which is what this file used first. A BigInt
 * is an allocation per character, and SimHash hashes every character about three
 * times over: on the 12 MB evidence fixture one signing pass held the main thread
 * for about five seconds, and an edit made three. The halves compute the same
 * function bit for bit; `similarity.reference.test.ts` keeps the BigInt version
 * and holds this one to it, because a stored signature is a file format.
 */
const OFFSET_HI = 0xcbf29ce4
const OFFSET_LO = 0x84222325
/** The prime is 2^40 + 0x1b3. Only the small term needs a multiply; the 2^40
 *  term is the low half shifted eight bits into the high one. */
const PRIME_LOW = 0x1b3
const SPACE = 0x20

/** Words per shingle. Three, because that is what makes a signature about
 *  PHRASING rather than about topic: two articles on one study share most of
 *  their vocabulary and almost none of their word triples, so a low distance
 *  between 3-shingle signatures means near-verbatim text and not a shared
 *  subject. One-word shingles would merge every article about composting. */
export const SHINGLE_WORDS = 3

/**
 * The floor under `simHash`, in folded characters.
 *
 * Below it a signature is not stable enough to act on: a handful of shingles
 * leaves most of the 64 accumulators at or near zero, so single-word edits flip
 * bits wholesale and two unrelated snippets can land close by luck. Short text
 * is not left unprotected — `textHash` still collapses exact copies of it, which
 * is the arm that catches verbatim syndication regardless of length.
 */
export const SIMHASH_MIN_CHARS = 280

/**
 * How close two signatures must be to count as one artefact: 10 bits of 64.
 *
 * Because the shingles are three words long the signature is phrasing-specific,
 * so a distance this small says the two texts are near-verbatim — the same story
 * under different boilerplate, a re-headed press release, a mirror with its own
 * navigation chrome — rather than two accounts of one subject.
 *
 * It errs toward merging, deliberately and for the reason `url.ts` gives about
 * its own suffix list: the two errors are not equally bad. Over-merging costs a
 * genuine refutation its loud channel, which the reader SEES — the reading lands
 * as `mixed`, `claimedReading` preserves what was claimed, and both sources stay
 * on screen with their domains. Under-merging hands whoever syndicated the text
 * the most destructive reading in the app, and `taint.ts` carries it down the
 * whole subtree, invisibly.
 */
export const SYNDICATION_HAMMING_MAX = 10

/**
 * Lowercase, collapse whitespace, trim. THE definition, with one caller pair that
 * matters: both sides of the quote check go through it, so a line break in the
 * fetched page cannot make a correctly-copied quote look fabricated — and both
 * signatures below are taken over the SAME folded string the quote check read, so
 * the record cannot claim a hash of text nobody compared.
 */
export const foldText = (s: string): string => s.toLowerCase().replace(/\s+/g, ' ').trim()

/**
 * Whether a quote is on the page it cites, as a tri-state — the one definition,
 * read by the source check and the prior-art search alike.
 *
 * `undefined` means the check was impossible: no page text came back (a snippet,
 * a PDF that arrived as base64, a provider that returns none), no quote was
 * offered, or the page was CUT and the quote is not in the part that arrived.
 * "Not in the text this app received" is not "not on the page", and `false` there
 * would accuse a model of inventing a quote it copied correctly. Only a quote
 * checked against text this app can see, and absent from it, is `false`.
 *
 * `page` is the page's text ALREADY folded, so a caller that reads the folded text
 * for anything else folds it once. The whole proposed quote is compared: a
 * retained prefix must never hide a fabricated suffix.
 */
export function quoteFoundOn(
  page: string | undefined,
  quote: string,
  truncated?: boolean,
): boolean | undefined {
  const found = page !== undefined && quote ? page.includes(foldText(quote)) : undefined
  return found === false && truncated === true ? undefined : found
}

/** 16 hex characters, always — a signature of a fixed width is a signature that
 *  can be validated on the way off disk without knowing what produced it. */
const hex = (hi: number, lo: number): string =>
  (hi >>> 0).toString(16).padStart(8, '0') + (lo >>> 0).toString(16).padStart(8, '0')

/** A 64-bit signature, or nothing. Anything that is not exactly 16 hex characters
 *  never came from this file. */
const SIGNATURE = /^[0-9a-f]{16}$/i

/**
 * The running hash, high and low halves. Module state rather than a returned pair
 * because SimHash hashes one shingle per word, and a pair allocated per shingle is
 * the cost this representation exists to remove. Nothing outside this file reads
 * them, and nothing that sets them yields before it has read them back.
 */
let hashHi = 0
let hashLo = 0

/**
 * Fold `s[from, to)` into the running hash: FNV-1a, 64-bit, over UTF-16 code units.
 * Iterating code units rather than code points is deliberate: it is exactly
 * reproducible and the pair of texts being compared is always folded the same way,
 * so a surrogate pair costs nothing.
 *
 * The multiply is done in sixteen-bit pieces so every intermediate is an exact
 * small integer: the low half times 0x1b3 carries into the high half, which also
 * takes the prime's 2^40 term. Everything above bit 63 falls away, as the mask did.
 */
function absorb(s: string, from: number, to: number): void {
  let hi = hashHi
  let lo = hashLo
  for (let i = from; i < to; i++) {
    lo ^= s.charCodeAt(i)
    const low = (lo & 0xffff) * PRIME_LOW
    const high = (lo >>> 16) * PRIME_LOW + (low >>> 16)
    hi = (Math.imul(hi, PRIME_LOW) + (high >>> 16) + (lo << 8)) | 0
    lo = (high << 16) | (low & 0xffff)
  }
  hashHi = hi
  hashLo = lo
}

/** One separator between shingle words, exactly as `join(' ')` would put it there. */
function absorbSpace(): void {
  const lo = hashLo ^ SPACE
  const low = (lo & 0xffff) * PRIME_LOW
  const high = (lo >>> 16) * PRIME_LOW + (low >>> 16)
  hashHi = (Math.imul(hashHi, PRIME_LOW) + (high >>> 16) + (lo << 8)) | 0
  hashLo = (high << 16) | (low & 0xffff)
}

/**
 * The primitive itself, as 16 hex characters. A SEAM, and the only reason it is
 * exported separately from the two keys below.
 *
 * `engine/liveSources.ts` caps a fetched record's id to what the codec stores,
 * and a cap that merely truncates can turn two works into one identity. It needs
 * a HASH and not a text identity, so it takes this rather than `textHash`: a call
 * site reading `textHash(docId)` would say a docId joins the syndication collapse
 * this file exists for, which it does not. Exported rather than copied, because a
 * second FNV in the tree is a second FNV to keep in step with the header above.
 */
export function fnv1a64(s: string): string {
  hashHi = OFFSET_HI
  hashLo = OFFSET_LO
  absorb(s, 0, s.length)
  return hex(hashHi, hashLo)
}

/**
 * The same 64 bits as a `bigint`, and the only other seam out of this primitive.
 *
 * `core/embed.ts` hashes on the order of a thousand feature strings per passage
 * and needs a BUCKET and a SIGN out of each, not a text identity — so the hex
 * round trip `fnv1a64` performs would be formatted and immediately re-parsed, at
 * roughly twice the cost of the hashing itself. It takes this for the same reason
 * `liveSources.ts` takes `fnv1a64`: a second FNV in the tree is a second FNV to
 * keep in step with the header above. Both halves are read back unsigned, because
 * `absorb` carries them as signed 32-bit words and the sign bit is bit 63 here.
 */
export function fnv1a(s: string): bigint {
  hashHi = OFFSET_HI
  hashLo = OFFSET_LO
  absorb(s, 0, s.length)
  return (BigInt(hashHi >>> 0) << 32n) | BigInt(hashLo >>> 0)
}

/**
 * The exact-duplicate key: one hash of the whole folded text.
 *
 * This is the arm that catches wire-service syndication, which republishes bytes
 * rather than paraphrases, and it works at any length. It is also what a later
 * pass can compare a re-fetch against to see that a page has been edited under a
 * citation — the same key, read for drift instead of for duplication.
 */
export const textHash = (folded: string): string => fnv1a64(folded)

/**
 * The near-duplicate signature: SimHash over word shingles.
 *
 * Each shingle votes on all 64 bits, +1 for a set bit and −1 for a clear one, and
 * a bit survives only where the votes are positive. That is what makes the result
 * a locality-sensitive signature rather than a hash: change the boilerplate around
 * a story and most shingles — and therefore most votes — are unchanged, so the
 * signature moves by a few bits instead of entirely.
 *
 * A tied accumulator resolves to 0. Ties are rare, and the rule has to be stated
 * rather than left to a comparison, or the signature stops being reproducible
 * from the stored text. Counted here as set bits against the shingle count — a
 * bit's votes are positive exactly when more than half its shingles set it — so a
 * tie is exactly half, and stays 0.
 *
 * The words are what `split(' ').filter(Boolean)` would give — runs between
 * single spaces, empty runs dropped — found as offsets, so a shingle is hashed in
 * place instead of being built as a string first.
 *
 * `undefined` below `SIMHASH_MIN_CHARS`, and `undefined` means "no signature" to
 * every caller — never "no similarity" and never "similar". A missing signature
 * must merge nothing.
 */
export function simHash(folded: string): string | undefined {
  if (folded.length < SIMHASH_MIN_CHARS) return undefined
  const starts: number[] = []
  const ends: number[] = []
  let at = 0
  while (at < folded.length) {
    if (folded.charCodeAt(at) === SPACE) {
      at++
      continue
    }
    starts.push(at)
    while (at < folded.length && folded.charCodeAt(at) !== SPACE) at++
    ends.push(at)
  }
  if (starts.length < SHINGLE_WORDS) return undefined

  const shingles = starts.length - SHINGLE_WORDS + 1
  const set = new Int32Array(64)
  for (let i = 0; i < shingles; i++) {
    hashHi = OFFSET_HI
    hashLo = OFFSET_LO
    for (let w = i; w < i + SHINGLE_WORDS; w++) {
      if (w > i) absorbSpace()
      absorb(folded, starts[w]!, ends[w]!)
    }
    const hi = hashHi
    const lo = hashLo
    for (let b = 0; b < 32; b++) {
      set[b]! += (lo >>> b) & 1
      set[b + 32]! += (hi >>> b) & 1
    }
  }

  let hi = 0
  let lo = 0
  for (let b = 0; b < 32; b++) {
    if (set[b]! * 2 > shingles) lo |= 1 << b
    if (set[b + 32]! * 2 > shingles) hi |= 1 << b
  }
  return hex(hi, lo)
}

/** Set bits in a 32-bit word. */
function popcount(x: number): number {
  x -= (x >>> 1) & 0x55555555
  x = (x & 0x33333333) + ((x >>> 2) & 0x33333333)
  x = (x + (x >>> 4)) & 0x0f0f0f0f
  return Math.imul(x, 0x01010101) >>> 24
}

/**
 * Bit distance between two signatures, 0–64.
 *
 * Anything that is not a pair of 16-hex signatures scores 64 — the maximum — so
 * an UNREADABLE signature behaves exactly as an ABSENT one does: it merges
 * nothing. The two must not come to mean different things, or a hand-edited
 * document would collapse sources a stored one keeps apart. This is not the
 * asymmetry's exception: a document whose fields can be hand-edited can simply
 * assert `refuted` outright, so nothing here is the control that stops it.
 */
export function hamming(aHex: string, bHex: string): number {
  if (!SIGNATURE.test(aHex) || !SIGNATURE.test(bHex)) return 64
  const hi = parseInt(aHex.slice(0, 8), 16) ^ parseInt(bHex.slice(0, 8), 16)
  const lo = parseInt(aHex.slice(8), 16) ^ parseInt(bHex.slice(8), 16)
  return popcount(hi) + popcount(lo)
}
